// SPDX-License-Identifier: AGPL-3.0-or-later

import type {AvatarCostumeColorOption, AvatarCostumePaints} from '@app/features/ui/avatars/AvatarCostumeResolvers';
import {
	COLOR_RANDOM_POOL,
	COSTUME_COLOR_DEFAULT_ID,
	COSTUME_COLOR_RANDOM_ID,
} from '@fluxer/constants/src/HalloweenCostumeConstants';

export {COLOR_RANDOM_POOL, COSTUME_COLOR_DEFAULT_ID, COSTUME_COLOR_RANDOM_ID};

/**
 * Shared dyes (after Default). Ids persist across costume switches.
 * `accent` is the picker swatch — horns, buckles, stars.
 * `accentDark` is the darker companion — hat bodies and silhouettes (orange hats remap the body onto the swatch).
 * `accentSoft` is the lighter companion — hat bands, bunny interiors.
 */
export const COSTUME_DYES: ReadonlyArray<AvatarCostumeColorOption> = [
	{id: 'black', accent: '#121414', accentDark: '#000000', accentSoft: '#3a3a3a'},
	{id: 'orange', accent: '#d35400', accentDark: '#8a3600', accentSoft: '#f0a060'},
	{id: 'purple', accent: '#6b2d9b', accentDark: '#3d1860', accentSoft: '#a878c8'},
	{id: 'green', accent: '#246b38', accentDark: '#153f22', accentSoft: '#5a9a6a'},
	{id: 'blood', accent: '#c40014', accentDark: '#860011', accentSoft: '#c45a6e'},
];

export type CostumeDyeId = 'black' | 'blood' | 'purple' | 'orange' | 'green';

/** Per-dye slot patches. Unspecified slots keep the shared dye. */
export type CostumeDyeOverrides = Partial<Record<CostumeDyeId, Partial<AvatarCostumePaints>>>;

export function paintsFromDye(dye: AvatarCostumeColorOption, overrides?: CostumeDyeOverrides): AvatarCostumePaints {
	const patch = overrides?.[dye.id as CostumeDyeId];
	return {
		accent: patch?.accent ?? dye.accent,
		accentDark: patch?.accentDark ?? dye.accentDark,
		accentSoft: patch?.accentSoft ?? dye.accentSoft,
	};
}

/** Picker order: Default chip + dyes + Randomize. Default is not a dye row in COSTUME_DYES. */
export const COSTUME_SWATCH_IDS: ReadonlyArray<string> = [
	COSTUME_COLOR_DEFAULT_ID,
	...COSTUME_DYES.map((dye) => dye.id),
	COSTUME_COLOR_RANDOM_ID,
];

const DYE_BY_ID = new Map(COSTUME_DYES.map((option) => [option.id, option]));

/** Old stored ids → current dye id, or null for Default. */
const LEGACY_COLOR_ID_MAP: Record<string, string | null> = {
	white: null,
	red: 'blood',
	wine: 'blood',
	forest: 'green',
	slate: 'black',
};

export function dyeById(id: string): AvatarCostumeColorOption | undefined {
	return DYE_BY_ID.get(id);
}

/** `null` / `'default'` → Default. Unknown ids → Randomize, same as the `random` sentinel. */
export function normalizeCostumeColorId(colorId: string | null | undefined): string | null {
	if (colorId == null || colorId === COSTUME_COLOR_DEFAULT_ID) return null;
	if (colorId === COSTUME_COLOR_RANDOM_ID) return COSTUME_COLOR_RANDOM_ID;
	if (Object.hasOwn(LEGACY_COLOR_ID_MAP, colorId)) {
		return LEGACY_COLOR_ID_MAP[colorId] ?? null;
	}
	if (DYE_BY_ID.has(colorId)) return colorId;
	return COSTUME_COLOR_RANDOM_ID;
}
